import { gql } from '@bariendo/lib/graphql';
import { LAST_COMMUNICATION_CATEGORIES } from './services/pacQueueHydration';
import {
  Track,
  BariatricProcedureStage,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
  PROCEDURE_APPOINTMENT_CODES,
  APPOINTMENT_TYPES_SYSTEM,
  TRACK_CODESYSTEM,
  BenefitAdministrator,
  BENEFIT_ADMINISTRATOR_CODESYSTEM,
} from '@bariendo/lib/constants';

const BODY_WEIGHT_LOINC_CODE = 'http://loinc.org|29463-7';
const BODY_HEIGHT_LOINC_CODE = 'http://loinc.org|8302-2';
const BODY_MASS_INDEX_LOINC_CODE = 'http://loinc.org|39156-5';

/**
 * URL of the PlanDefinition for the APP - Lab review task.
 * This is used to filter the tasks for the patient list page.
 */
const APP_LAB_REVIEW_PLAN_DEFINITION_URL = 'http://bariendo.com/fhir/PlanDefinition/app-lab-review';

/**
 * _filter expression matching any procedure appointment type.
 * Expands PROCEDURE_APPOINTMENT_CODES into OR-joined `eq` clauses because
 * _filter does not support the `:text` modifier.
 */
const PROCEDURE_APPOINTMENT_TYPE_FILTER = PROCEDURE_APPOINTMENT_CODES.map(
  (code) => `appointment-type eq \\"${APPOINTMENT_TYPES_SYSTEM}|${code}\\"`
).join(' or ');

const PATIENT_FRAGMENT = gql`
  fragment PatientFragment on Patient {
    id
    resourceType
    name {
      family
      given
      use
    }
    birthDate
    telecom {
      system
      value
      use
      rank
      extension {
        url
        valueString
      }
    }
    active
    extension {
      url
      valueString
    }
    meta {
      tag {
        code
        system
        extension {
          url
          valueDateTime
        }
      }
    }
    lastCommunication: CommunicationList(
      _reference: subject,
      _count: 1,
      _sort: "-sent",
      status: "completed",
      category: "${LAST_COMMUNICATION_CATEGORIES.join(',')}"
    ) {
      id
      resourceType
      sent
      # WARNING — exactly ONE union branch here, deliberately.
      #
      # This fragment is used by PATIENT_LIST_QUERY, whose MY_PATIENTS variant
      # roots on a '_has:CareTeam:subject:participant' filter. A '_has'-filtered
      # root that selects TWO OR MORE branches of a union field returns zero rows
      # with no error (see CARRUM_PATIENT_LIST_QUERY's note). Adding a second
      # branch here would silently empty the My patients tab.
      sender {
        display
        reference
        resource {
          ... on Practitioner {
            resourceType
            id
            name {
              given
              family
            }
          }
        }
      }
    }
  }
`;

const PRACTITIONER_WITH_ROLES_FRAGMENT = gql`
  fragment PractitionerWithRolesFragment on Practitioner {
    resourceType
    id
    name {
      given
      family
    }
    practitionerRoles: PractitionerRoleList(_reference: practitioner, _count: 1) {
      code {
        coding {
          system
          code
          display
        }
      }
    }
  }
`;

/**
 * `PatientFragment` without its `lastCommunication` chain, for the three CI
 * queues that load their whole cohort (BAR-2738).
 *
 * A nested reverse-chain is charged per row — measured on production
 * (2026-09-28) at ~20 of a user's 50,000 points a minute each, so the two the
 * All inquiry query carried spent ~38k loading one 1,152-patient cohort, and a
 * rep reopening the tab hit "Too Many Requests". The queues read Last
 * communication from bulk hydration instead (`lastCommunicationSince`). Do not
 * add a nested list here without re-measuring against production.
 */
const QUEUE_PATIENT_FRAGMENT = gql`
  fragment QueuePatientFragment on Patient {
    id
    resourceType
    name {
      family
      given
      use
    }
    birthDate
    telecom {
      system
      value
      use
      rank
      extension {
        url
        valueString
      }
    }
    active
    extension {
      url
      valueString
      # PATIENT_CREATED_AT_EXTENSION — All inquiry's sign-up date (BAR-2787).
      valueDateTime
      # PAYMENT_STATUS_EXTENSION's status, at and payor (BAR-3084).
      extension {
        url
        valueCode
        valueString
        valueDateTime
      }
    }
    meta {
      tag {
        code
        system
        extension {
          url
          valueDateTime
        }
      }
    }
  }
`;

/**
 * A participant Practitioner's name, for the queues. Not
 * `PractitionerWithRolesFragment`: the queue rows never read the roles, and its
 * `PractitionerRoleList` chain is another per-row charge.
 */
const QUEUE_PRACTITIONER_FRAGMENT = gql`
  fragment QueuePractitionerFragment on Practitioner {
    resourceType
    id
    name {
      given
      family
    }
  }
`;

const PROCEDURE_APPOINTMENT_PARTICIPANT_FRAGMENT = gql`
  fragment ProcedureAppointmentParticipantFragment on AppointmentParticipant {
    type {
      coding {
        system
        code
        display
      }
    }
    actor {
      display
      reference
      resource {
        ...PractitionerWithRolesFragment
      }
    }
  }
`;

const PROCEDURE_APPOINTMENT_FIELDS_FRAGMENT = gql`
  fragment ProcedureAppointmentFieldsFragment on Appointment {
    id
    start
    status
    appointmentType {
      coding {
        code
        display
      }
    }
    participant {
      ...ProcedureAppointmentParticipantFragment
    }
  }
`;

export const PATIENT_LIST_QUERY = gql`
  ${PATIENT_FRAGMENT}

  query SearchPatients($name: String, $filter: String, $_tag: String) {
    PatientList(_sort: "name", name: $name, _filter: $filter, _tag: $_tag) {
      ...PatientFragment
    }
  }
`;

export const MY_UPCOMING_APPOINTMENTS_QUERY = gql`
  ${PATIENT_FRAGMENT}

  query SearchAppointments($practitioner: String, $filter: String) {
    AppointmentList(practitioner: $practitioner, _sort: "-date", _filter: $filter) {
      start
      status
      resourceType
      appointmentType {
        coding {
          system
          code
          display
        }
      }
      participant {
        actor {
          resource {
            ... on Patient {
              ...PatientFragment
              resourceType
            }
          }
        }
      }
    }
  }
`;

/**
 * Appointment-rooted queries for "All..." patient list filters.
 * Patient cohort is resolved via REST (stage tag + optional name); `patientIds` scopes AppointmentList.
 */
export const ALL_CONSULTATION_APPOINTMENTS_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${PRACTITIONER_WITH_ROLES_FRAGMENT}

  query AllConsultationAppointments($patientIds: String) {
    AppointmentList(
      patient: $patientIds,
      _sort: "-date",
      _filter: "(date pr true and appointment-type eq \"${APPOINTMENT_TYPES_SYSTEM}|app-initial-consultation-visit\" and status ne \"cancelled\" and status ne \"entered-in-error\")"
    ) {
      id
      start
      status
      appointmentType {
        coding {
          code
          display
        }
      }
      participant {
        type {
          coding {
            system
            code
            display
          }
        }
        actor {
          display
          reference
          resource {
            ...PractitionerWithRolesFragment
            ... on Patient {
              ...PatientFragment
            }
          }
        }
      }
    }
  }
`;

export const ALL_PRE_PROCEDURE_APPOINTMENTS_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${PRACTITIONER_WITH_ROLES_FRAGMENT}

  query AllPreProcedureAppointments($patientIds: String) {
    AppointmentList(
      patient: $patientIds,
      _sort: "-date",
      _filter: "(date pr true and (${PROCEDURE_APPOINTMENT_TYPE_FILTER}) and status ne \"cancelled\" and status ne \"entered-in-error\")"
    ) {
      id
      start
      status
      appointmentType {
        coding {
          code
          display
        }
      }
      participant {
        type {
          coding {
            system
            code
            display
          }
        }
        actor {
          display
          reference
          resource {
            ...PractitionerWithRolesFragment
            ... on Patient {
              ...PatientFragment
              medicationRequests: MedicationRequestList(_reference: subject, _count: 1) {
                id
                status
              }
              latestLabOrder: DiagnosticReportList(_reference: subject, _sort: "-issued", _count: 1) {
                id
                status
              }
              labReviewTasks: TaskList(
                _reference: patient,
                _sort: "-authored-on",
                status: "completed",
                _filter: "based-on:PlanDefinition.url eq \"${APP_LAB_REVIEW_PLAN_DEFINITION_URL}\""
              ) {
                id
                encounter {
                  reference
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const ALL_POST_PROCEDURE_APPOINTMENTS_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${PRACTITIONER_WITH_ROLES_FRAGMENT}

  query AllPostProcedureAppointments($patientIds: String) {
    AppointmentList(
      patient: $patientIds,
      _sort: "-date",
      _filter: "(date pr true and (${PROCEDURE_APPOINTMENT_TYPE_FILTER}) and status ne \"cancelled\" and status ne \"entered-in-error\")"
    ) {
      id
      start
      status
      appointmentType {
        coding {
          code
          display
        }
      }
      participant {
        type {
          coding {
            system
            code
            display
          }
        }
        actor {
          display
          reference
          resource {
            ...PractitionerWithRolesFragment
            ... on Patient {
              ...PatientFragment
              lastWeightObservation: ObservationList(
                _reference: subject,
                code: "${BODY_WEIGHT_LOINC_CODE}",
                _sort: "-date",
                _count: 1
              ) {
                effectiveDateTime
                valueQuantity {
                  value
                  code
                }
              }
              firstWeightObservation: ObservationList(
                _reference: subject,
                code: "${BODY_WEIGHT_LOINC_CODE}",
                _sort: "date",
                _count: 1
              ) {
                effectiveDateTime
                valueQuantity {
                  value
                  code
                }
              }
              startingBmi: ObservationList(
                _reference: subject,
                code: "${BODY_MASS_INDEX_LOINC_CODE}",
                _sort: "date",
                _count: 1
              ) {
                effectiveDateTime
                valueQuantity {
                  value
                }
              }
              latestBmi: ObservationList(
                _reference: subject,
                code: "${BODY_MASS_INDEX_LOINC_CODE}",
                _sort: "-date",
                _count: 1
              ) {
                effectiveDateTime
                valueQuantity {
                  value
                }
              }
              latestHeight: ObservationList(
                _reference: subject,
                code: "${BODY_HEIGHT_LOINC_CODE}",
                _sort: "-date",
                _count: 1
              ) {
                effectiveDateTime
                valueQuantity {
                  value
                  code
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * The pre-consultation CI queue, rooted on the intro call.
 *
 * Every other "All ..." tab resolves its cohort by paging every Patient carrying
 * a stage tag and passing the ids comma-joined into GraphQL. That does not scale
 * here: this tab's population spans `pre-consultation-unpaid`, the largest
 * bucket in the project. Rooting on the appointment removes the pre-fetch
 * entirely and lets the range picker scope the query server-side — so the
 * reworked tab is *cheaper* than the one it replaces, not dearer.
 *
 * NO `_count` on the root list. `useLiveGraphQL` injects `_count` and `_offset`
 * into the top-level list field on every fetch, paginated or not — setting one
 * here as well makes the server reject the whole query with "There can be only
 * one argument named _count", which surfaces as an empty tab. Control the bound
 * with the hook's `pageSize` option instead. Nested lists are untouched by the
 * injector and keep their own `_count`.
 *
 * No nested reverse-chains: each is charged per row, and this query loads a
 * whole cohort — see `QUEUE_PATIENT_FRAGMENT`. Each union selection below
 * picks exactly ONE branch, per the `_has` + union rule noted on
 * `CARRUM_PATIENT_LIST_QUERY`.
 */
export const ALL_PRE_CONSULTATION_APPOINTMENTS_QUERY = gql`
  ${QUEUE_PATIENT_FRAGMENT}
  ${QUEUE_PRACTITIONER_FRAGMENT}

  query AllPreConsultationAppointments($filter: String, $sort: String = "-date") {
    AppointmentList(_sort: $sort, _filter: $filter) {
      id
      resourceType
      # The hub's paged tab keeps only each patient's latest-consult (BAR-2880).
      meta {
        tag {
          system
          code
        }
      }
      start
      end
      status
      created
      appointmentType {
        coding {
          system
          code
          display
        }
      }
      # Rooted on the appointment, not the patient: an intro call, or — since
      # Intake 1.1 — the consult a lead self-scheduled without one.
      #
      # Location and Likelihood come from the note for THIS call, via basedOn —
      # not the patient's latest note. A lead who took a second intro call should
      # read against the call the row is about.
      participant {
        actor {
          display
          reference
          resource {
            ...QueuePractitionerFragment
            ... on Patient {
              # Carries the hub's filter tags, for rows that arrive by subscription (BAR-2890).
              ...QueuePatientFragment
              # Earliest episode start is the signup date. Deliberately not
              # RESOURCE_CREATION_DATE_EXTENSION, which is re-stamped on every
              # stage change, nor history version 1, which is the Healthie import
              # date for migrated patients — wrong for exactly the oldest leads.
            }
          }
        }
      }
    }
  }
`;

/**
 * The post-consultation CI queue, rooted on the consult.
 *
 * Same inversion as the pre-consultation query, for the same reasons: no
 * patient-id pre-fetch, and the range picker bounds the query server-side.
 *
 * No `_count` on the root list — see the note on the pre-consultation query.
 *
 * The three consult-note columns read live QuestionnaireResponses from the
 * APP - Consultation H&P PlanDefinition, fetched by canonical URL rather than by
 * walking the Task that produced them. That choice predates BAR-2585, when CI's
 * Task grant was scoped to group-identifier=email-inbox and the Task route was
 * unreadable for this tab's users; the grant now covers orbit-provider-task, but
 * the canonical-URL read stays because it is simpler and needs no Task at all.
 */
export const ALL_POST_CONSULTATION_CONSULTS_QUERY = gql`
  ${QUEUE_PATIENT_FRAGMENT}
  ${QUEUE_PRACTITIONER_FRAGMENT}

  query AllPostConsultationConsults($filter: String) {
    AppointmentList(_sort: "-date", _filter: $filter) {
      id
      resourceType
      # The hub's paged tab keeps only each patient's latest-consult (BAR-2865).
      meta {
        tag {
          system
          code
        }
      }
      start
      end
      status
      # Decides which chart note carries the row's next step: an NP consult's
      # H&P answers, or a PN consultation's Outcome.
      appointmentType {
        coding {
          system
          code
          display
        }
      }
      participant {
        actor {
          display
          reference
          resource {
            ...QueuePractitionerFragment
            ... on Patient {
              # Carries the hub's filter tags, for rows that arrive by subscription (BAR-2890).
              ...QueuePatientFragment
              # Drops patients who already had their procedure — CI has nothing
              # left to sell them. Payment is the precise boundary, but payment
              # state is not on this query and the procedure is.
              # Open clinical tasks, for the Next task column and the Overdue
              # chip. group_identifier is snake_case here — Medplum's GraphQL
              # layer converts it to the REST group-identifier, and a hyphen in
              # an argument name is a syntax error.
            }
          }
        }
      }
    }
  }
`;

/**
 * The All inquiry queue, rooted on the Patient.
 *
 * A self-pay sign-up creates a Patient and nothing else — no `EpisodeOfCare`
 * (that is a partner-track enrolment, Carrum-only in practice), no stage tag
 * (the bot runs on payment and appointment webhooks, not on sign-up), no
 * appointment. So the only server-searchable trace of "signed up this week" is
 * `_lastUpdated`, which `buildInquiryPatientFilter` bounds together with
 * `_tag ne` for every stage past Inquiry. That scope is a superset; the rows
 * are narrowed by each patient's sign-up date, from hydration — see
 * `inquiryRows` for the sources and their order.
 *
 * No `_count` on the root list — see the note on the pre-consultation query.
 */
/** The Financing and approval tab (BAR-2829), paged by `useQueuePage`; the fragment carries the stage tag. */
export const FINANCING_PATIENTS_QUERY = gql`
  ${QUEUE_PATIENT_FRAGMENT}

  query FinancingPatients($filter: String) {
    PatientList(_sort: "-_lastUpdated", _filter: $filter) {
      ...QueuePatientFragment
    }
  }
`;

export const ALL_INQUIRY_PATIENTS_QUERY = gql`
  ${QUEUE_PATIENT_FRAGMENT}

  query AllInquiryPatients($filter: String) {
    PatientList(_sort: "-_lastUpdated", _filter: $filter) {
      # Nothing nested. The eligibility submission, which used to ride along as a
      # per-row chain, arrives through bulk hydration with Last communication —
      # see QUEUE_PATIENT_FRAGMENT for what the chains cost.
      # Carries the Patient's tags too: the hub's membership check for rows that
      # arrive by subscription (BAR-2877).
      ...QueuePatientFragment
    }
  }
`;

export const ALL_PRE_CONSULTATION_OR_POST_CONSULTATION_PATIENT_LIST_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${PRACTITIONER_WITH_ROLES_FRAGMENT}
  ${PROCEDURE_APPOINTMENT_PARTICIPANT_FRAGMENT}
  ${PROCEDURE_APPOINTMENT_FIELDS_FRAGMENT}

  query AllConsultationPatients($name: String, $_tag: String) {
    PatientList(_sort: "name", name: $name, _tag: $_tag) {
      ...PatientFragment
      relatedAppointment: AppointmentList(
        _reference: patient,
        _sort: "-date",
        _count: 1,
        _filter: "(date pr true and appointment-type eq \"${APPOINTMENT_TYPES_SYSTEM}|app-initial-consultation-visit\" and status ne \"cancelled\" and status ne \"entered-in-error\")"
      ) {
        ...ProcedureAppointmentFieldsFragment
      }
    }
  }
`;

export const ALL_PRE_PROCEDURE_PATIENT_LIST_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${PRACTITIONER_WITH_ROLES_FRAGMENT}
  ${PROCEDURE_APPOINTMENT_PARTICIPANT_FRAGMENT}
  ${PROCEDURE_APPOINTMENT_FIELDS_FRAGMENT}

  query AllPreProcedurePatients($name: String) {
    PatientList(_sort: "name", name: $name, _tag: "${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.PRE_PROCEDURE}") {
      ...PatientFragment
      medicationRequests: MedicationRequestList(
        _reference: subject,
        _count: 1
      ) {
        id
        status
      }
      latestLabOrder: DiagnosticReportList(
        _reference: subject,
        _sort: "-issued",
        _count: 1
      ) {
        id
        status
      }
      labReviewTasks: TaskList(
        _reference: patient,
        _sort: "-authored-on",
        status: "completed",
        _filter: "based-on:PlanDefinition.url eq \"${APP_LAB_REVIEW_PLAN_DEFINITION_URL}\""
      ) {
        id
        encounter {
          reference
        }
      }
      relatedAppointment: AppointmentList(
        _reference: patient,
        _sort: "-date",
        _count: 1,
        _filter: "(date pr true and (${PROCEDURE_APPOINTMENT_TYPE_FILTER}) and status ne \"cancelled\" and status ne \"entered-in-error\")"
      ) {
        ...ProcedureAppointmentFieldsFragment
      }
    }
  }
`;

export const ALL_POST_PROCEDURE_PATIENT_LIST_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${PRACTITIONER_WITH_ROLES_FRAGMENT}
  ${PROCEDURE_APPOINTMENT_PARTICIPANT_FRAGMENT}
  ${PROCEDURE_APPOINTMENT_FIELDS_FRAGMENT}

  query AllPostProcedurePatients($name: String) {
    PatientList(_sort: "name", name: $name, _tag: "${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.POST_PROCEDURE}") {
      ...PatientFragment
      relatedAppointment: AppointmentList(
        _reference: patient,
        _sort: "-date",
        _count: 1,
        _filter: "(date pr true and (${PROCEDURE_APPOINTMENT_TYPE_FILTER}) and status ne \"cancelled\" and status ne \"entered-in-error\")"
      ) {
        ...ProcedureAppointmentFieldsFragment
      }
      lastWeightObservation: ObservationList(
        _reference: subject,
        code: "${BODY_WEIGHT_LOINC_CODE}",
        _sort: "-date",
        _count: 1
      ) {
        effectiveDateTime
        valueQuantity {
          value
          code
        }
      }
      firstWeightObservation: ObservationList(
        _reference: subject,
        code: "${BODY_WEIGHT_LOINC_CODE}",
        _sort: "date",
        _count: 1
      ) {
        effectiveDateTime
        valueQuantity {
          value
          code
        }
      }
      startingBmi: ObservationList(
        _reference: subject,
        code: "${BODY_MASS_INDEX_LOINC_CODE}",
        _sort: "date",
        _count: 1
      ) {
        effectiveDateTime
        valueQuantity { value }
      }
      latestBmi: ObservationList(
        _reference: subject,
        code: "${BODY_MASS_INDEX_LOINC_CODE}",
        _sort: "-date",
        _count: 1
      ) {
        effectiveDateTime
        valueQuantity { value }
      }
    }
  }
`;

/**
 * Carrum reporting tab: patient-rooted query for the signups tracker.
 * Cohort is scoped by the `_has` Carrum filter supplied via `$filter` at call time
 * (see usePatientList); this query only threads it through.
 *
 * Modeled on `ALL_PRE_CONSULTATION_OR_POST_CONSULTATION_PATIENT_LIST_QUERY` (patient-rooted,
 * reverse-associated `AppointmentList(_reference: patient, ...)`) and the weight-observation
 * selections from `ALL_POST_PROCEDURE_PATIENT_LIST_QUERY` (copied verbatim).
 *
 * The patient's Carrum `EpisodeOfCare` is reverse-associated the same way, via
 * `EpisodeOfCareList(_reference: patient, ...)` — EpisodeOfCare has a standard FHIR `patient`
 * search parameter, so the same sub-selection mechanism used for AppointmentList/ObservationList
 * applies. Filtered to `type` = the track CodeSystem's `carrum` code OR the benefit-administrator
 * CodeSystem's, so the right episode is returned even if the patient has switched tracks. Both
 * codings are matched for the BAR-2602 compatibility window: a legacy episode carries `carrum` as
 * its track, a new one carries `employer` plus a `carrum` administrator coding. Comma is OR in a
 * token search, and a coding nothing carries yet simply contributes no rows.
 *
 * The procedure appointment's participant selection intentionally selects ONLY the Location
 * branch (`... on Location { resourceType name address { city state } }`) — the Carrum tab only
 * needs the procedure Location's city, never the Practitioner. It must NOT also spread
 * `...PractitionerWithRolesFragment`: when this PatientList query is filtered with a root `_has`
 * reverse-chain (the Carrum cohort filter), selecting a SECOND branch on the `actor.resource`
 * union (either `...PractitionerWithRolesFragment` or a bare `resourceType` alongside
 * `... on Location`) makes the Medplum GraphQL server deterministically return ZERO root rows
 * (verified against the live server: Location-only → all rows; Location + any sibling → 0).
 * `resourceType` is selected INSIDE the Location branch so `resource.resourceType === 'Location'`
 * still works as the discriminator (GraphQL only returns selected fields for the resolved type;
 * non-Location participants resolve to an empty object and are skipped by consumers).
 */
export const CARRUM_PATIENT_LIST_QUERY = gql`
  ${PATIENT_FRAGMENT}

  query CarrumPatientList($name: String, $filter: String) {
    PatientList(_sort: "name", name: $name, _filter: $filter) {
      ...PatientFragment
      address {
        city
        state
      }
      carrumEpisode: EpisodeOfCareList(
        _reference: patient,
        type: "${TRACK_CODESYSTEM}|${Track.CARRUM},${BENEFIT_ADMINISTRATOR_CODESYSTEM}|${BenefitAdministrator.CARRUM}",
        _sort: "-date",
        _count: 1
      ) {
        period {
          start
        }
        type {
          coding {
            system
            code
          }
        }
      }
      consultationAppointment: AppointmentList(
        _reference: patient,
        _sort: "-date",
        _count: 1,
        _filter: "(date pr true and appointment-type eq \"${APPOINTMENT_TYPES_SYSTEM}|app-initial-consultation-visit\" and status ne \"cancelled\" and status ne \"entered-in-error\")"
      ) {
        id
        start
      }
      procedureAppointment: AppointmentList(
        _reference: patient,
        _sort: "-date",
        _count: 1,
        _filter: "(date pr true and (${PROCEDURE_APPOINTMENT_TYPE_FILTER}) and status ne \"cancelled\" and status ne \"entered-in-error\")"
      ) {
        id
        start
        participant {
          type {
            coding {
              system
              code
              display
            }
          }
          actor {
            display
            reference
            resource {
              ... on Location {
                resourceType
                name
                address {
                  city
                  state
                }
              }
            }
          }
        }
      }
      lastWeightObservation: ObservationList(
        _reference: subject,
        code: "${BODY_WEIGHT_LOINC_CODE}",
        _sort: "-date",
        _count: 1
      ) {
        effectiveDateTime
        valueQuantity {
          value
          code
        }
      }
      firstWeightObservation: ObservationList(
        _reference: subject,
        code: "${BODY_WEIGHT_LOINC_CODE}",
        _sort: "date",
        _count: 1
      ) {
        effectiveDateTime
        valueQuantity {
          value
          code
        }
      }
    }
  }
`;
